import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const money = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} $`;

// Dialogue unique : action = "merge" (2 abonnements → 1 facture) ou "link" (1 carte → N abonnements). Aperçu Stripe avant confirmation.
export function BillingActionsDialog({ open, onClose, action, contractIds, payerEmail, onDone }) {
  const [mode, setMode] = useState("simple");
  const [keepId, setKeepId] = useState("");
  const [payer, setPayer] = useState(payerEmail || "");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPayer(payerEmail || ""); setKeepId(""); setPreview(null); setError(""); }, [payerEmail, contractIds, action, open]);

  useEffect(() => {
    if (!open || !contractIds?.length) return;
    if (action === "link" && !payer) return;
    setBusy(true); setError(""); setPreview(null);
    const req = action === "merge"
      ? api.post("/billing/merge/preview", { contract_ids: contractIds, mode, keep_contract_id: keepId || null })
      : api.post("/billing/link-card/preview", { contract_ids: contractIds, payer_email: payer });
    req.then((r) => setPreview(r.data)).catch((e) => setError(e?.response?.data?.detail || "Aperçu impossible")).finally(() => setBusy(false));
  }, [open, action, contractIds, mode, keepId, payer]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = action === "merge"
        ? await api.post("/billing/merge", { contract_ids: contractIds, mode, keep_contract_id: keepId || null, confirm: true })
        : await api.post("/billing/link-card", { contract_ids: contractIds, payer_email: payer, confirm: true });
      if (r.data.errors?.length) toast.warning(`Terminé avec ${r.data.errors.length} erreur(s) : ${r.data.errors[0]}`);
      else toast.success(action === "merge" ? `Fusion réussie · 1 abonnement · ${money(r.data.new_monthly_total)}/mois · prochaine facture ${r.data.next_renewal}` : `Carte •••• ${r.data.card.last4} reliée à ${r.data.done.length} abonnement(s)`);
      onDone?.(r.data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); } finally { setBusy(false); }
  };

  const allContracts = preview?.keep ? [...preview.keep.contracts, ...preview.merged.flatMap((m) => m.contracts)] : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto" data-testid="billing-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-400">{action === "merge" ? "⛓ Fusionner en 1 seul abonnement Stripe" : "💳 Relier une carte à plusieurs abonnements"}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {action === "merge" ? "Une seule facture par mois pour tous les contrats sélectionnés. Jamais de double facturation." : "La carte enregistrée du payeur devient la méthode de paiement par défaut de chaque abonnement."}
            {preview?.simulated && <span className="ml-2 text-[10px] uppercase tracking-widest text-fuchsia-300 border border-fuchsia-500/40 rounded px-1.5 py-0.5" data-testid="billing-simulated-badge">Aperçu simulé (préversion)</span>}
          </DialogDescription>
        </DialogHeader>

        {action === "merge" && (
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <label className={`rounded-lg border p-3 cursor-pointer ${mode === "simple" ? "border-amber-500 bg-amber-500/10" : "border-zinc-800"}`}>
              <input type="radio" className="mr-2" checked={mode === "simple"} onChange={() => setMode("simple")} data-testid="merge-mode-simple" />
              <b>Simple (recommandé)</b>
              <div className="text-xs text-zinc-400 mt-1">On garde l'abonnement au renouvellement le plus tard, on y ajoute l'autre ligne, on annule l'autre. Les jours entre les deux dates sont offerts.</div>
            </label>
            <label className={`rounded-lg border p-3 cursor-pointer ${mode === "proration" ? "border-amber-500 bg-amber-500/10" : "border-zinc-800"}`}>
              <input type="radio" className="mr-2" checked={mode === "proration"} onChange={() => setMode("proration")} data-testid="merge-mode-proration" />
              <b>Proration Stripe exacte</b>
              <div className="text-xs text-zinc-400 mt-1">Crédit des jours non utilisés + facturation au prorata sur la prochaine facture (lignes d'ajustement visibles par le membre).</div>
            </label>
          </div>
        )}

        {action === "link" && (
          <div className="text-sm">
            <label className="text-xs text-zinc-400">Courriel du payeur (carte enregistrée)</label>
            <input value={payer} onChange={(e) => setPayer(e.target.value)} placeholder="parent@exemple.com" data-testid="link-payer-email"
              className="mt-1 w-full rounded-lg bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm" />
          </div>
        )}

        {busy && !preview && <p className="text-zinc-400 text-sm" data-testid="billing-preview-loading">Lecture Stripe…</p>}
        {error && <div className="rounded-lg border border-red-800 bg-red-950/40 text-red-200 text-sm p-3" data-testid="billing-preview-error">{error}</div>}

        {preview && action === "merge" && (
          <div className="space-y-3 text-sm" data-testid="merge-preview">
            <div className="rounded-lg border border-emerald-800 bg-emerald-950/30 p-3">
              <div className="text-xs uppercase tracking-widest text-emerald-300">Abonnement conservé (principal)</div>
              <div className="font-semibold mt-1">{preview.keep.contracts.map((c) => `${c.full_name}${c.discipline ? ` · ${c.discipline}` : ""}`).join(", ")}</div>
              <div className="text-zinc-300 text-xs mt-1">{preview.keep.subscription_id} · {money(preview.keep.monthly)}/mois · prochaine facture <b>{preview.keep.next_renewal || "—"}</b></div>
              <label className="block mt-2 text-xs text-zinc-400">Changer le principal :
                <select value={keepId} onChange={(e) => setKeepId(e.target.value)} className="ml-2 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs" data-testid="merge-keep-select">
                  <option value="">Automatique (renouvellement le plus tard)</option>
                  {allContracts.map((c) => <option key={c.id} value={c.id}>{c.full_name}{c.discipline ? ` · ${c.discipline}` : ""}</option>)}
                </select>
              </label>
            </div>
            {preview.merged.map((m) => (
              <div key={m.subscription_id} className={`rounded-lg border p-3 ${m.double_billing_risk ? "border-red-700 bg-red-950/30" : "border-zinc-800 bg-zinc-900/40"}`} data-testid={`merge-line-${m.subscription_id}`}>
                <div className="font-semibold">{m.contracts.map((c) => `${c.full_name}${c.discipline ? ` · ${c.discipline}` : ""}`).join(", ")} → ajouté au principal</div>
                <div className="text-zinc-300 text-xs mt-1">{m.subscription_id} · {money(m.monthly)}/mois · payé jusqu'au {m.paid_until || "—"} · sera annulé</div>
                <div className={`text-xs mt-1 ${m.double_billing_risk ? "text-red-300" : "text-amber-200"}`}>{m.effect}</div>
              </div>
            ))}
            {preview.warnings?.map((w, i) => <div key={i} className="text-xs text-amber-300">⚠️ {w}</div>)}
            <div className="rounded-lg border border-amber-600/60 bg-amber-500/10 p-3 flex items-center justify-between">
              <span>Nouvelle facture mensuelle unique</span><b className="text-amber-300 text-lg" data-testid="merge-new-total">{money(preview.new_monthly_total)}</b>
            </div>
          </div>
        )}

        {preview && action === "link" && (
          <div className="space-y-3 text-sm" data-testid="link-preview">
            <div className="rounded-lg border border-emerald-800 bg-emerald-950/30 p-3">
              <div className="text-xs uppercase tracking-widest text-emerald-300">Carte du payeur</div>
              <div className="font-semibold mt-1">{String(preview.card.brand || "").toUpperCase()} •••• {preview.card.last4} · exp {preview.card.exp_month}/{preview.card.exp_year}</div>
            </div>
            {preview.actions.map((a) => (
              <div key={a.subscription_id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid={`link-line-${a.subscription_id}`}>
                <div className="font-semibold">{a.contracts.join(", ")} <span className={`ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${a.action === "relier" ? "bg-emerald-800/60 text-emerald-200" : a.action === "deplacer" ? "bg-amber-800/60 text-amber-200" : "bg-zinc-800 text-zinc-300"}`}>{a.action}</span></div>
                <div className="text-zinc-300 text-xs mt-1">{a.subscription_id}{a.monthly !== undefined ? ` · ${money(a.monthly)}/mois · prochaine facture ${a.next_renewal || "—"}` : ""}</div>
                <div className="text-xs text-zinc-400 mt-1">{a.why}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-zinc-700 text-sm" data-testid="billing-cancel-btn">Annuler</button>
          <button onClick={confirm} disabled={busy || !preview || preview.double_billing_risk} data-testid="billing-confirm-btn"
            title={preview?.double_billing_risk ? "Risque de double facturation — change l'abonnement principal ou passe en mode proration" : ""}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-bold text-sm">
            {busy ? "…" : action === "merge" ? "Confirmer la fusion" : "Relier la carte"}
          </button>
        </div>
        {preview?.double_billing_risk && <p className="text-xs text-red-300 text-right" data-testid="billing-risk-hint">Bouton désactivé : risque de double facturation — change l'abonnement principal ou passe en mode proration.</p>}
      </DialogContent>
    </Dialog>
  );
}
